import { useEffect, useState, useRef } from 'react';
import { useStore, OperationalPoint, TimeSlot, type OfferProduct, type OfferProductCategoryQuota, type CustomFieldDefinition, type TeamCustomFieldValue } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';

// Convert DB rows to Frontend Models
function mapCategory(row: any) {
  return {
    id: row.id,
    name: row.name,
    isGeneral: row.is_general,
    isArtistCategory: row.is_artist_category || false,
    isPublic: row.is_public || false,
    managerToken: row.manager_token,
    managerEmails: row.manager_emails,
    orgId: row.org_id,
    projectId: row.project_id,
    managerEmail: row.manager_email,
    managerEmailConfirmed: row.manager_email_confirmed || false,
    managerVerificationCode: row.manager_verification_code,
    managerVerificationCodeSentAt: row.manager_verification_code_sent_at,
  };
}

function mapTeam(row: any) {
  return {
    id: row.id,
    name: row.name,
    orgId: row.org_id,
    role: row.role,
    roles: row.roles,
    color: row.color,
    email: row.email,
    phone: row.phone,
    categoryId: row.category_id,
    teamToken: row.staff_token,
    accessPassword: row.access_password,
    photo: row.photo_url,
    isOnDuty: row.is_on_duty,
    unavailableDates: row.unavailable_dates || [],
    customButtons: row.custom_buttons || [],
    status: row.status ?? 'approved',
    excludeFromMagicFill: row.exclude_from_magic_fill ?? false,
    specialties: row.specialties || [],
    isDistributor: row.is_distributor ?? false,
    distAllowScan: row.dist_allow_scan ?? false,
    distAllowDirect: row.dist_allow_direct ?? false,
    distAllowCheckin: row.dist_allow_checkin ?? false,
    distAllowLinkWallet: row.dist_allow_link_wallet ?? false,
    distAllowView: row.dist_allow_view ?? false,
    isArtist: row.is_artist ?? false,
    isProfileVisible: row.is_profile_visible ?? false,
    emailConfirmed: row.email_confirmed ?? false,
    verificationCode: row.verification_code,
    verificationCodeSentAt: row.verification_code_sent_at,
    publicDescription: row.public_description,
    publicSkills: row.public_skills || [],
    indicativeDailyRate: row.indicative_daily_rate,
    geographicalZone: row.geographical_zone,
    pwaInstalled: row.pwa_installed ?? false,
    fcmToken: row.fcm_token,
    linkedWalletQr: row.linked_wallet_qr ?? null,
    memberCount: row.member_count ?? 1,
    parentArtistId: row.parent_artist_id ?? undefined,
    memberIndex: row.member_index ?? undefined,
    categoryIds: row.category_ids || [],
    isCategorySupervisor: row.is_category_supervisor ?? false,
    assignedTeamId: row.assigned_team_id ?? undefined,
    disableMessageEmails: row.disable_message_emails ?? false,
  };
}

function mapOfferProduct(row: any) {
  return {
    id: row.id,
    name: row.name,
    icon: row.icon ?? 'Beer',
    defaultQuota: row.default_quota ?? 0,
    pointsValue: row.points_value ?? 1,
    orgId: row.org_id,
    projectId: row.project_id,
  };
}

function mapOfferProductCategoryQuota(row: any) {
  return {
    id: row.id,
    productId: row.product_id,
    categoryId: row.category_id,
    quota: row.quota ?? 5,
    orgId: row.org_id,
    projectId: row.project_id,
  };
}

function mapTeamCustomQuota(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    productName: row.product_name,
    customQuota: row.custom_quota,
    orgId: row.org_id,
    projectId: row.project_id,
  };
}

function mapTeamConsumption(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    productName: row.product_name,
    quantity: row.quantity ?? 1,
    pointsValue: row.points_value ?? 1,
    scannedBy: row.scanned_by,
    pointId: row.point_id ?? undefined,
    createdAt: row.created_at,
    orgId: row.org_id,
    projectId: row.project_id,
    deletedAt: row.deleted_at ?? null,
    deletedBy: row.deleted_by ?? null,
  };
}

function mapTeamCheckin(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    pointId: row.point_id,
    scannedBy: row.scanned_by,
    createdAt: row.created_at,
    orgId: row.org_id,
    projectId: row.project_id,
    deletedAt: row.deleted_at ?? null,
    deletedBy: row.deleted_by ?? null,
  };
}


function mapPoint(row: any, hoursRows: any[]) {
  const hours: TimeSlot[] = hoursRows
    .filter(h => h.point_id === row.id)
    .map(h => ({
      start: h.start_time.substring(0, 16),
      end: h.end_time.substring(0, 16),
      requiredTeam: h.required_staff,
      isHard: h.is_hard,
    }));

  return {
    id: row.id,
    name: row.name,
    categoryId: row.category_id,
    orgId: row.org_id,
    projectId: row.project_id,
    iconName: row.icon_name,
    location: row.location_lat ? { lat: row.location_lat, lng: row.location_lng } : undefined,
    requiredTeam: row.required_staff,
    openingHours: hours,
    swapEnabled: row.swap_enabled,
    isMealPoint: row.is_meal_point,
    unavailabilityEnabled: row.unavailability_enabled,
    isMapFocus: row.is_map_focus,
    descriptionMarkdown: row.description_markdown ?? undefined,
    isRestricted: row.is_restricted ?? false,
    isStage: row.is_stage ?? false,
  } as OperationalPoint;
}

function mapShift(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    pointId: row.point_id,
    orgId: row.org_id,
    projectId: row.project_id,
    start: row.start_time.substring(0, 16),
    end: row.end_time.substring(0, 16),
    description: row.description,
    actualStart: row.actual_start ?? undefined,
    actualEnd: row.actual_end ?? undefined,
    shiftType: row.shift_type ?? 'standard',
    proposedAmount: row.proposed_amount,
    agreedAmount: row.agreed_amount,
    amountNegotiatedByTeam: row.amount_negotiated_by_staff ?? false,
    proposedCurrency: row.proposed_currency ?? 'TND',
    agreementStatus: row.agreement_status ?? 'approved',
  };
}

function mapMessage(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    orgId: row.org_id,
    projectId: row.project_id,
    content: row.content,
    read: row.read,
    fromTeam: row.from_staff ?? false,
    timestamp: row.created_at,
    senderName: row.sender_name ?? undefined,
    recipientTeamId: row.recipient_staff_id ?? undefined,
    imageUrl: row.image_url ?? null,
    voiceUrl: row.voice_url ?? null,
  };
}

function mapApprovalRequest(row: any) {
  return {
    id: row.id,
    teamId: row.staff_id,
    teamName: '', // resolved later from team array
    categoryId: row.category_id,
    orgId: row.org_id,
    projectId: row.project_id,
    content: row.content,
    imageUrl: row.image_url ?? null,
    voiceUrl: row.voice_url ?? null,
    recipientIds: row.recipient_ids || [],
    recipientNames: row.recipient_names || [],
    status: row.status,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

function mapTask(row: any, hoursRows: any[]) {
  const hours: TimeSlot[] = hoursRows
    .filter(h => h.task_id === row.id)
    .map(h => ({
      start: h.start_time.substring(0, 16),
      end: h.end_time.substring(0, 16),
      requiredTeam: h.required_staff,
      isHard: h.is_hard,
    }));
  return {
    id: row.id, name: row.name, categoryId: row.category_id,
    orgId: row.org_id, projectId: row.project_id,
    requiredTeam: row.required_staff, iconName: row.icon_name,
    descriptionMarkdown: row.description_markdown ?? undefined,
    openingHours: hours,
  } as import('../store/useStore').Task;
}

function mapIssue(row: any) {
  return {
    id: row.id,
    pointId: row.point_id,
    teamId: row.staff_id,
    orgId: row.org_id,
    projectId: row.project_id,
    teamName: "Unknown",
    message: row.description,
    resolved: row.resolved,
    timestamp: row.created_at,
    type: row.type || 'alert'
  };
}

function mapOrganization(row: any) {
  return {
    id: row.id,
    name: row.name,
    adminToken: row.admin_token,
    logoUrl: row.logo_url,
    allowAdminSettings: row.allow_admin_settings || false,
    adminEmail: row.admin_email,
    settings: row.settings || { minRestTimeHours: 8 },
    createdAt: row.created_at
  };
}

function mapCustomFieldDefinition(row: any): CustomFieldDefinition {
  return {
    id: row.id,
    orgId: row.org_id,
    label: row.label,
    fieldType: row.field_type,
    options: row.options || [],
    required: row.required,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  };
}

function mapTeamCustomFieldValue(row: any): TeamCustomFieldValue {
  return {
    id: row.id,
    fieldId: row.field_id,
    teamId: row.staff_id,
    value: row.value,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function useSupabaseSync(session: Session | null, isInitializing: boolean = false): { isSynced: boolean; refresh: () => void } {
  const [isSynced, setIsSynced] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const loadDataRef = useRef<((isInitial?: boolean) => Promise<void>) | null>(null);
  const lastSyncTime = useRef<number>(0); // cache 2 minutes pour éviter les re-syncs en cascade

  const { currentOrgId, currentProjectId } = useStore();

  useEffect(() => {
    if (isInitializing) return;

    let isMounted = true;
    async function loadData(isInitial = false) {
      if (isInitial && !initialLoadDone) setIsSynced(false);

      // Cache 2 minutes : ignorer les re-syncs trop rapprochés (ex: changement d'onglet)
      if (!isInitial && Date.now() - lastSyncTime.current < 120000) return;

      try {
        // 1. Fetch Organizations (RLS will filter for non-superadmins)
        const { data: orgs } = await supabase.from('organizations').select('*').is('deleted_at', null);
        const orgsList = (orgs || []).map(mapOrganization);

        // 2. Resolve orgId from token if missing in store but present in localStorage
        let effectiveOrgId = useStore.getState().currentOrgId;
        if (!effectiveOrgId) {
          const token = localStorage.getItem('wfm_org_token');
          if (token) {
            const { data: orgData } = await supabase.from('organizations').select('id').eq('admin_token', token).maybeSingle();
            if (orgData?.id) effectiveOrgId = orgData.id;
          }
        }
        if (!effectiveOrgId) {
          effectiveOrgId = session?.user?.user_metadata?.org_id || null;
        }

        // 3. Fetch all projects for the organization (or all if superadmin)
        let projectsQuery = supabase.from('projects').select('*').is('deleted_at', null);
        if (effectiveOrgId) {
          projectsQuery = projectsQuery.eq('org_id', effectiveOrgId);
        }
        const { data: projs } = await projectsQuery;
        const mappedProjects = (projs || []).map(row => ({
          id: row.id,
          orgId: row.org_id,
          name: row.name,
          startDate: row.start_date,
          endDate: row.end_date,
          createdAt: row.created_at
        }));

                // 4. Determine currentProjectId
        let activeProjectId = useStore.getState().currentProjectId;
        const role = session?.user?.user_metadata?.role || 'staff';
        // Pre-determine team ID for project scoping decisions
        const activeTeamId = session?.user?.user_metadata?.staff_id || null;
        // For team users, always skip project scoping so they can see all their shifts
        const isTeamUser = role === 'staff' && activeTeamId !== null;
        const isSuperAdmin = session?.user?.email?.toLowerCase().trim() === 'benmloukaamen@gmail.com' ||
                             (typeof window !== 'undefined' && localStorage.getItem('wfm_user_email') === 'benmloukaamen@gmail.com');
        const isOrgAdmin = role === 'admin' && !isSuperAdmin;
        
        if (role === 'manager') {
          const mToken = localStorage.getItem('wfm_manager_token');
          if (mToken) {
            const { data: managerRecs } = await supabase.rpc('resolve_manager_token', { p_token: mToken });
            if (managerRecs && managerRecs.length > 0 && managerRecs[0].project_id) {
              activeProjectId = managerRecs[0].project_id;
            }
          }
        }

        const orgProjects = mappedProjects.filter(p => p.orgId === effectiveOrgId);
        if (role !== 'manager' && !isOrgAdmin && (!activeProjectId || !orgProjects.some(p => p.id === activeProjectId))) {
          const storedProjId = localStorage.getItem('wfm_project_id');
          if (storedProjId && orgProjects.some(p => p.id === storedProjId)) {
            activeProjectId = storedProjId;
          } else {
            const nowStr = new Date().toISOString().split('T')[0];
            const ongoingOrFuture = orgProjects.filter(p => !p.endDate || p.endDate >= nowStr);
            if (ongoingOrFuture.length > 0) {
              ongoingOrFuture.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
              activeProjectId = ongoingOrFuture[0].id;
            } else if (orgProjects.length > 0) {
              orgProjects.sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
              activeProjectId = orgProjects[0].id;
            } else {
              activeProjectId = null;
            }
          }
        }

                        useStore.setState({
          projects: mappedProjects,
          currentProjectId: isTeamUser ? null : activeProjectId
        });
        if (activeProjectId && !isTeamUser && !isOrgAdmin) {
          localStorage.setItem('wfm_project_id', activeProjectId);
        }

                // 5. Build queries for project-scoped tables
        let categoriesQuery = supabase.from('categories').select('*').is('deleted_at', null);
        let pointsQuery = supabase.from('points').select('*').is('deleted_at', null);
        let tasksQuery = supabase.from('tasks').select('*').is('deleted_at', null);
        let taskHoursQuery = supabase.from('task_opening_hours').select('*');
        let shiftsQuery = supabase.from('shifts').select('*').is('deleted_at', null);
        let messagesQuery = supabase.from('messages').select('*').is('deleted_at', null).limit(300).order('created_at', { ascending: false });
        let approvalRequestsQuery = supabase.from('message_approval_requests').select('*').is('deleted_at', null);
        let orgAdminEmailsQuery = supabase.from('organization_admins').select('email, org_id').eq('email_confirmed', true).is('deleted_at', null);
        let categoryManagerEmailsQuery = supabase.from('category_managers').select('email, category_id').eq('email_confirmed', true).is('deleted_at', null);
        let issuesQuery = supabase.from('issues').select('*').is('deleted_at', null);
        let offerProductsQuery = supabase.from('offer_products').select('*').is('deleted_at', null);
        let quotasQuery = supabase.from('staff_custom_quotas').select('*');
        let catQuotasQuery = supabase.from('offer_product_category_quotas').select('*');
        let consumptionsQuery = supabase.from('staff_consumptions').select('*').is('deleted_at', null).limit(500).order('created_at', { ascending: false });
        let checkinsQuery = supabase.from('staff_checkins').select('*').is('deleted_at', null).limit(500).order('created_at', { ascending: false });

        console.log('[Sync] role:', role, 'isSuperAdmin:', isSuperAdmin, 'isOrgAdmin:', isOrgAdmin, 'effectiveOrgId:', effectiveOrgId?.substring(0, 8) + '...', 'activeProjectId:', activeProjectId, 'orgProjects:', orgProjects.length);

                                                                                // Determine the active team member (for shared team across orgs)
                                // (already determined above as activeTeamId)

                // Pre-declare isSharedTeam - will be fully computed later after org discovery
                let isSharedTeam = false;
                // Note: preLoadedShifts is no longer used, fallback fetch happens after Promise.all

                                // Determine all orgs this team member is linked to:
                                // 1. From shared_team_organizations table (explicit org sharing)
                                // 2. From shifts that reference this team ID (implicit association)
                                const sharedOrgIds = new Set<string>();
                                if (effectiveOrgId) sharedOrgIds.add(effectiveOrgId);
        // Note: isSharedTeam is already declared above

                                // Fetch shared_team_organization links
                const { data: sharedLinks } = activeTeamId
                  ? await supabase.from('shared_staff_organizations').select('org_id').eq('staff_id', activeTeamId)
                  : { data: null };
                for (const link of (sharedLinks || [])) {
                  if (link.org_id) sharedOrgIds.add(link.org_id);
                }

                // Also discover orgs from shifts assigned to this team member
                // (they may have been assigned a shift directly without an explicit shared_team_organizations link)
                if (activeTeamId) {
                  const { data: teamShifts } = await supabase
                    .from('shifts')
                    .select('org_id')
                    .eq('staff_id', activeTeamId)
                    .is('deleted_at', null);
                  for (const s of (teamShifts || [])) {
                    if (s.org_id) sharedOrgIds.add(s.org_id);
                  }
                }

                                                // For team users who work across multiple orgs, we need to load data from ALL their orgs.
                // CRITICAL: ALWAYS set shiftsQuery to fetch ALL shifts for this team (no org filter)
                // so that the team can see their shifts regardless of which org created them.
                isSharedTeam = isTeamUser;

        if (isSuperAdmin) {
          console.log('[Sync] BRANCH: superadmin — query by effectiveOrgId, no project filter');
          // Superadmin does not filter project-scoped collections by activeProjectId in DB queries
          if (effectiveOrgId) {
            categoriesQuery = categoriesQuery.eq('org_id', effectiveOrgId);
            pointsQuery = pointsQuery.eq('org_id', effectiveOrgId);
            tasksQuery = tasksQuery.eq('org_id', effectiveOrgId);
            shiftsQuery = shiftsQuery.eq('org_id', effectiveOrgId);
            messagesQuery = messagesQuery.eq('org_id', effectiveOrgId);
            approvalRequestsQuery = approvalRequestsQuery.eq('org_id', effectiveOrgId);
            issuesQuery = issuesQuery.eq('org_id', effectiveOrgId);
            offerProductsQuery = offerProductsQuery.eq('org_id', effectiveOrgId);
            quotasQuery = quotasQuery.eq('org_id', effectiveOrgId);
            catQuotasQuery = catQuotasQuery.eq('org_id', effectiveOrgId);
            consumptionsQuery = consumptionsQuery.eq('org_id', effectiveOrgId);
            checkinsQuery = checkinsQuery.eq('org_id', effectiveOrgId);
          }
        } else if (isOrgAdmin) {
          if (activeProjectId && effectiveOrgId) {
            console.log('[Sync] BRANCH: orgAdmin — query by project_id:', activeProjectId);
            categoriesQuery = categoriesQuery.eq('project_id', activeProjectId);
            pointsQuery = pointsQuery.eq('project_id', activeProjectId);
            tasksQuery = tasksQuery.eq('project_id', activeProjectId);
            shiftsQuery = shiftsQuery.eq('project_id', activeProjectId);
            messagesQuery = messagesQuery.eq('project_id', activeProjectId);
            approvalRequestsQuery = approvalRequestsQuery.eq('project_id', activeProjectId);
            issuesQuery = issuesQuery.eq('project_id', activeProjectId);
            offerProductsQuery = offerProductsQuery.eq('project_id', activeProjectId);
            quotasQuery = quotasQuery.eq('project_id', activeProjectId);
            catQuotasQuery = catQuotasQuery.eq('project_id', activeProjectId);
            consumptionsQuery = consumptionsQuery.eq('project_id', activeProjectId);
            checkinsQuery = checkinsQuery.eq('project_id', activeProjectId);
          } else if (effectiveOrgId) {
            console.log('[Sync] BRANCH: orgAdmin — query by effectiveOrgId, no project filter');
            categoriesQuery = categoriesQuery.eq('org_id', effectiveOrgId);
            pointsQuery = pointsQuery.eq('org_id', effectiveOrgId);
            tasksQuery = tasksQuery.eq('org_id', effectiveOrgId);
            shiftsQuery = shiftsQuery.eq('org_id', effectiveOrgId);
            messagesQuery = messagesQuery.eq('org_id', effectiveOrgId);
            approvalRequestsQuery = approvalRequestsQuery.eq('org_id', effectiveOrgId);
            issuesQuery = issuesQuery.eq('org_id', effectiveOrgId);
            offerProductsQuery = offerProductsQuery.eq('org_id', effectiveOrgId);
            quotasQuery = quotasQuery.eq('org_id', effectiveOrgId);
            catQuotasQuery = catQuotasQuery.eq('org_id', effectiveOrgId);
            consumptionsQuery = consumptionsQuery.eq('org_id', effectiveOrgId);
            checkinsQuery = checkinsQuery.eq('org_id', effectiveOrgId);
          } else {
            console.log('[Sync] BRANCH: orgAdmin — NO ORG RESOLVED, forcing empty');
            categoriesQuery = categoriesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            pointsQuery = pointsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            shiftsQuery = shiftsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            messagesQuery = messagesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            issuesQuery = issuesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            offerProductsQuery = offerProductsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            quotasQuery = quotasQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            consumptionsQuery = consumptionsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            checkinsQuery = checkinsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          }
                } else if (isSharedTeam) {
          console.log('[Sync] BRANCH: sharedTeam — query by team_id across orgs');
                  // For team users: ALWAYS load shifts by team_id (across all orgs)
                  // so they can see all their shifts regardless of which org created them.
                  // Also load points, categories etc from all relevant orgs.
                  const sharedOrgIdsArray = Array.from(sharedOrgIds);
                  categoriesQuery = categoriesQuery.in('org_id', sharedOrgIdsArray);
                  pointsQuery = pointsQuery.in('org_id', sharedOrgIdsArray);
                  // Load ALL shifts from the orgs where this team member is active
                  // (so they can see colleagues' shifts at the same points)
                  shiftsQuery = supabase.from('shifts').select('*').is('deleted_at', null).in('org_id', sharedOrgIdsArray);
          messagesQuery = messagesQuery.in('org_id', sharedOrgIdsArray);
          approvalRequestsQuery = approvalRequestsQuery.in('org_id', sharedOrgIdsArray);
          issuesQuery = issuesQuery.in('org_id', sharedOrgIdsArray);
          offerProductsQuery = offerProductsQuery.in('org_id', sharedOrgIdsArray);
          quotasQuery = quotasQuery.in('org_id', sharedOrgIdsArray);
          consumptionsQuery = consumptionsQuery.in('org_id', sharedOrgIdsArray);
          checkinsQuery = checkinsQuery.in('org_id', sharedOrgIdsArray);
        } else if (activeProjectId) {
          console.log('[Sync] BRANCH: admin/manager — query by project_id:', activeProjectId);
          categoriesQuery = categoriesQuery.eq('project_id', activeProjectId);
          pointsQuery = pointsQuery.eq('project_id', activeProjectId);
          shiftsQuery = shiftsQuery.eq('project_id', activeProjectId);
          messagesQuery = messagesQuery.eq('project_id', activeProjectId);
          approvalRequestsQuery = approvalRequestsQuery.eq('project_id', activeProjectId);
          issuesQuery = issuesQuery.eq('project_id', activeProjectId);
          offerProductsQuery = offerProductsQuery.eq('project_id', activeProjectId);
          quotasQuery = quotasQuery.eq('project_id', activeProjectId);
          consumptionsQuery = consumptionsQuery.eq('project_id', activeProjectId);
          checkinsQuery = checkinsQuery.eq('project_id', activeProjectId);
        } else {
          console.log('[Sync] BRANCH: NO PROJECT — forcing empty queries (no org, no project, not superadmin)');
          // If no active project exists for the organization yet, force empty queries
          categoriesQuery = categoriesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          pointsQuery = pointsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          shiftsQuery = shiftsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          messagesQuery = messagesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          approvalRequestsQuery = approvalRequestsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          issuesQuery = issuesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          offerProductsQuery = offerProductsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          quotasQuery = quotasQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          consumptionsQuery = consumptionsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          checkinsQuery = checkinsQuery.eq('id', '00000000-0000-0000-0000-000000000000');
        }

        // 6. Parallel fetching
        const queries = [
          categoriesQuery,
          supabase.from('staff').select('*').is('deleted_at', null),
          pointsQuery,
          supabase.from('point_opening_hours').select('*'),
          tasksQuery,
          taskHoursQuery,
          shiftsQuery,
          messagesQuery,
          issuesQuery,
          supabase.from('global_settings').select('*').limit(1).maybeSingle(),
          offerProductsQuery,
          quotasQuery,
          catQuotasQuery,
          consumptionsQuery,
          checkinsQuery,
          supabase.from('shared_staff_organizations').select('*'),
          supabase.from('custom_field_definitions').select('*').eq('org_id', effectiveOrgId).order('sort_order'),
          supabase.from('staff_custom_field_values').select('*'),
          orgAdminEmailsQuery,
          categoryManagerEmailsQuery,
        ];

        const [
          { data: cats },
          { data: stf },
          { data: pts },
          { data: hrs },
          { data: taskRows },
          { data: taskHrs },
          { data: shf },
          { data: msg },
          { data: iss },
          { data: gsRow },
          { data: prodRows },
          { data: quotaRows },
          { data: catQuotaRows },
          { data: consRows },
          { data: checkRows },
          { data: sharedTeamOrgsRow },
          { data: fieldDefRows },
          { data: fieldValueRows },
          { data: orgAdminRows },
          { data: catManagerRows },
        ] = await Promise.all(queries) as any[];

        console.log('[Sync] DB results — shifts:', shf?.length, 'points:', pts?.length, 'team:', stf?.length, 'cats:', cats?.length, 'org_id filter:', effectiveOrgId);

                // Safety retry: if everything is empty but we have an org context, retry once in 1.2s
                if (effectiveOrgId && activeProjectId && (!stf || stf.length === 0) && (!pts || pts.length === 0)) {
                   console.log('[Sync] Empty results with org context, retrying...');
                   setTimeout(() => setInitialLoadDone(false), 1200);
                   return;
                }

                // For team users: RPC get_staff_shifts bypasses RLS, more reliable than parallel query
                let finalShiftRows = shf;
                if (isTeamUser && activeTeamId) {
                  try {
                    const { data: rpcResult } = await supabase.rpc('get_staff_shifts', { p_staff_id: activeTeamId });
                    if (rpcResult && rpcResult.length > 0) {
                      finalShiftRows = rpcResult;
                      console.log('[Sync] RPC get_staff_shifts:', rpcResult.length, 'shifts (parallel had', (shf || []).length, ')');
                    }
                  } catch (rpcErr) {
                    console.warn('[Sync] RPC get_staff_shifts failed, using parallel result:', rpcErr);
                  }
                }

        // For org admins (anonymous session): use SECURITY DEFINER RPC to bypass RLS
        if (isOrgAdmin && effectiveOrgId) {
          try {
            if (activeProjectId) {
              const { data: rpcShifts } = await supabase.rpc('get_project_shifts', { p_project_id: activeProjectId });
              if (rpcShifts) {
                console.log('[Sync] RPC get_project_shifts:', rpcShifts.length, 'shifts');
                finalShiftRows = rpcShifts;
              }
            } else {
              const { data: rpcShifts } = await supabase.rpc('get_org_shifts', { p_org_id: effectiveOrgId });
              if (rpcShifts) {
                console.log('[Sync] RPC get_org_shifts:', rpcShifts.length, 'shifts');
                finalShiftRows = rpcShifts;
              }
            }
          } catch (rpcErr) {
            console.warn('[Sync] RPC org shifts failed:', rpcErr);
          }

          // Also reload team via RPC to bypass RLS (same issue as shifts)
          try {
            const { data: rpcTeam } = await supabase.rpc('get_org_staff', { p_org_id: effectiveOrgId });
            if (rpcTeam && rpcTeam.length > 0) {
              console.log('[Sync] RPC get_org_staff:', rpcTeam.length, 'team (was', (stf || []).length, ')');
              stf.length = 0;
              stf.push(...rpcTeam);
            }
          } catch (rpcErr) {
            console.warn('[Sync] RPC org team failed:', rpcErr);
          }
        }

                const currentOrgData = orgs?.filter((o: any) => o.id === (effectiveOrgId || '00000000-0000-0000-0000-000000000000')) || [];

                const mappedCategories = (cats || []).map(mapCategory);
                const mappedTeam = (stf || []).map(mapTeam);

                // ── Dedup team by email WITHIN an organisation: keep the most complete record ──
                // The same email may legitimately exist in two organisations (e.g. a pending
                // registration in one while the person is already a member in another), so the
                // organisation is part of the dedup key. Only duplicates inside the same
                // organisation are collapsed.
                const dedupedTeam = (() => {
                  const seen = new Map<string, any>(); // email|org -> team member
                  for (const member of mappedTeam) {
                    const email = member.email?.toLowerCase().trim();
                    if (!email) {
                      // No email: keep as-is (can't dedup)
                      seen.set(member.id, member);
                      continue;
                    }
                    const key = `${email}|${member.orgId || ''}`;
                    const existing = seen.get(key);
                    if (!existing) {
                      seen.set(key, member);
                      continue;
                    }
                    // Both have the same email — keep the most complete one
                    let existingScore = 0;
                    let memberScore = 0;
                    if (existing.categoryId) existingScore += 100;
                    if (member.categoryId) memberScore += 100;
                    if (existing.orgId) existingScore += 50;
                    if (member.orgId) memberScore += 50;
                    if ((existing.roles || []).length > 0) existingScore += 20;
                    if ((member.roles || []).length > 0) memberScore += 20;
                    if (existing.role) existingScore += 10;
                    if (member.role) memberScore += 10;
                    if (existing.emailConfirmed) existingScore += 5;
                    if (member.emailConfirmed) memberScore += 5;
                    if (existing.fcmToken) existingScore += 5;
                    if (member.fcmToken) memberScore += 5;
                    if (existing.photo) existingScore += 2;
                    if (member.photo) memberScore += 2;
                    if (existing.name && existing.name !== existing.email?.split('@')[0]) existingScore += 1;
                    if (member.name && member.name !== member.email?.split('@')[0]) memberScore += 1;

                    if (memberScore > existingScore) {
                      console.log('[Sync dedup] Replacing duplicate:', { email, orgId: member.orgId, kept: member.name, removed: existing.name });
                      seen.set(key, member);
                    } else {
                      console.log('[Sync dedup] Keeping existing over duplicate:', { email, orgId: member.orgId, kept: existing.name, removed: member.name });
                    }
                  }
                  return Array.from(seen.values());
                })();

                const mappedPoints = (pts || []).map((p: any) => mapPoint(p, hrs || []));
                const mappedTasks = (taskRows || []).map((t: any) => mapTask(t, taskHrs || []));
                // Use the fallback shifts if they were loaded (more complete)
                const mappedShifts = (finalShiftRows || []).map(mapShift);

        // DEBUG CRITIQUE: immediately log shifts from DB vs mapped
        if (isTeamUser && activeTeamId) {
          console.log('[Sync CRITICAL] Shift loading debug:', {
            rawShiftsCount: (shf || []).length,
            mappedShiftsCount: mappedShifts.length,
            myShiftsCount: mappedShifts.filter((s: any) => s.teamId === activeTeamId).length,
            rawOrgIds: [...new Set((shf || []).map((s: any) => s.org_id))],
            myRawShifts: (shf || []).filter((s: any) => s.staff_id === activeTeamId).map((s: any) => ({ id: s.id, org_id: s.org_id, start: s.start_time })),
          });
        }

                // DEBUG: Log what shifts were loaded for team users (shared team debug)
        if (isSharedTeam && activeTeamId) {
          console.log('[Sync] Team sync debug:', {
            activeTeamId,
            sharedOrgIds: Array.from(sharedOrgIds),
            shiftsLoaded: mappedShifts.length,
            shiftsForTeam: mappedShifts.filter((s: any) => s.teamId === activeTeamId),
            myShifts: mappedShifts.filter((s: any) => s.teamId === activeTeamId).map((s: any) => ({ id: s.id, orgId: s.orgId, pointId: s.pointId, start: s.start })),
            pointsLoaded: mappedPoints.length,
            pointsByOrg: mappedPoints.reduce((acc: any, p: any) => { acc[p.orgId] = (acc[p.orgId] || 0) + 1; return acc; }, {}),
          });
        }
        const mappedMessages = (msg || []).map(mapMessage);

        // Fetch approval requests separately (table may not exist yet — fail gracefully)
        let appReq: any[] = [];
        try {
          const { data: appReqData, error: appReqErr } = await approvalRequestsQuery;
          if (appReqErr) {
            console.warn('[Sync] message_approval_requests table not available:', appReqErr.message);
          } else {
            appReq = appReqData || [];
          }
        } catch (e: any) {
          console.warn('[Sync] message_approval_requests fetch failed (table may not exist):', e.message);
        }

        const mappedApprovalRequests = (appReq || [])
          .map(mapApprovalRequest)
          .map((req: any) => {
            const teamMember = dedupedTeam.find((s: any) => s.id === req.teamId);
            return { ...req, teamName: teamMember?.name || 'Inconnu' };
          })
          .filter((req: any) => new Date(req.expiresAt) > new Date()); // filter expired
        const mappedIssues = (iss || []).map((issueRow: any) => {
          const mapped = mapIssue(issueRow);
          const relatedTeam = dedupedTeam.find((s: any) => s.id === mapped.teamId);
          if (relatedTeam) {
            mapped.teamName = relatedTeam.name;
          }
          return mapped;
        });

        const mappedOfferProducts = (prodRows || []).map(mapOfferProduct);
        const mappedCategoryQuotas = (catQuotaRows || []).map(mapOfferProductCategoryQuota);
        // Embed category quotas directly into each product for reliable resolution
        const productsWithQuotas = mappedOfferProducts.map((prod: OfferProduct) => ({
          ...prod,
          categoryQuotas: mappedCategoryQuotas
            .filter((q: OfferProductCategoryQuota) => q.productId === prod.id)
            .map((q: OfferProductCategoryQuota) => ({ categoryId: q.categoryId, quota: q.quota }))
        }));
        const mappedCustomQuotas = (quotaRows || []).map(mapTeamCustomQuota);
        const mappedConsumptions = (consRows || []).map(mapTeamConsumption);
        const mappedCheckins = (checkRows || []).map(mapTeamCheckin);
        const mappedSharedTeamOrgs = (sharedTeamOrgsRow || []).map((row: any) => ({
          id: row.id,
          teamId: row.staff_id,
          orgId: row.org_id,
          createdAt: row.created_at
        }));

        const mappedCustomFieldDefs = (fieldDefRows || []).map(mapCustomFieldDefinition);
        const mappedTeamCustomFieldValues = (fieldValueRows || []).map(mapTeamCustomFieldValue);

        let globalSettings = { 
          minRestTimeHours: gsRow?.min_rest_time_hours || 8, 
          appLogoUrl: gsRow?.app_logo_url || undefined,
          mapProvider: (gsRow?.map_provider as 'mapbox' | 'google') || 'mapbox'
        };

        if (currentOrgData && currentOrgData.length > 0) {
          const settings = currentOrgData[0].settings || {};
          globalSettings = {
            minRestTimeHours: settings.min_rest_time_hours || globalSettings.minRestTimeHours,
            appLogoUrl: settings.app_logo_url || globalSettings.appLogoUrl,
            mapProvider: settings.map_provider || globalSettings.mapProvider || 'mapbox'
          };
        }

                // Skip NO PROJECT data only if we already have valid data (prevents React #300 overwrite)
                const currentState = useStore.getState();
                const isEmpty = mappedPoints.length === 0 && mappedShifts.length === 0 && mappedCategories.length === 0 && dedupedTeam.length > 0;
                const hasExistingData = currentState.points.length > 0 || currentState.shifts.length > 0 || currentState.categories.length > 0;
                if (isEmpty && hasExistingData) { lastSyncTime.current = Date.now(); setIsSynced(true); setInitialLoadDone(true); return; }
                useStore.setState({
          organizations: orgsList,
          categories: mappedCategories,
          team: dedupedTeam,
          points: isSharedTeam
            ? [...new Map([...useStore.getState().points, ...mappedPoints].map(p => [p.id, p])).values()]
            : mappedPoints,
          tasks: isSharedTeam
            ? [...new Map([...useStore.getState().tasks, ...mappedTasks].map(t => [t.id, t])).values()]
            : mappedTasks,
          shifts: isSharedTeam
            ? [...new Map([...useStore.getState().shifts, ...mappedShifts].map(s => [s.id, s])).values()]
            : mappedShifts,
          messages: mappedMessages,
          approvalRequests: mappedApprovalRequests,
          orgAdminEmails: (orgAdminRows || []).map((r: any) => ({ email: r.email, orgId: r.org_id })),
          categoryManagerEmails: (catManagerRows || []).map((r: any) => ({ email: r.email, categoryId: r.category_id })),
          issues: mappedIssues,
          globalSettings: globalSettings,
          offerProducts: productsWithQuotas,
          offerProductCategoryQuotas: mappedCategoryQuotas,
          customQuotas: mappedCustomQuotas,
          consumptions: mappedConsumptions,
          checkins: mappedCheckins,
          sharedTeamOrganizations: mappedSharedTeamOrgs,
          customFieldDefinitions: mappedCustomFieldDefs,
          teamCustomFieldValues: mappedTeamCustomFieldValues,
        });

        // Initialize Map Focus on startup/load
        const currentFocus = useStore.getState().mapFocus;
        if (!currentFocus && mappedPoints.length > 0) {
          let targetPoint: any = null;
          const teamId = session?.user?.user_metadata?.staff_id;

          if (role === 'staff' && teamId) {
            const now = new Date();
            const myShifts = mappedShifts
              .filter((s: any) => s.teamId === teamId)
              .sort((a: any, b: any) => new Date(a.start).getTime() - new Date(b.start).getTime());

            // Try to find current active or first upcoming shift
            const activeOrFutureShift = myShifts.find((s: any) => new Date(s.end) > now) || myShifts[0];
            if (activeOrFutureShift) {
              targetPoint = mappedPoints.find((p: any) => p.id === activeOrFutureShift.pointId);
            }
          }

          if (!targetPoint) {
            const nowTime = Date.now();
            let minDiff = Infinity;
            mappedPoints.forEach((point: any) => {
              if (!point.location) return;
              point.openingHours?.forEach((slot: any) => {
                if (!slot.start) return;
                const diff = Math.abs(new Date(slot.start).getTime() - nowTime);
                if (diff < minDiff) {
                  minDiff = diff;
                  targetPoint = point;
                }
              });
            });
          }

          if (targetPoint && targetPoint.location) {
            useStore.setState({
              mapFocus: {
                lat: targetPoint.location.lat,
                lng: targetPoint.location.lng,
                zoomLevel: 17
              }
            });
          }
        }

      } catch (err) {
        console.error("Failed to load normalized data", err);
      } finally {
        if (isMounted) {
          lastSyncTime.current = Date.now();
          setIsSynced(true);
          setInitialLoadDone(true);
        }
      }
    }

    loadDataRef.current = loadData;
    loadData(true);

    // Polling léger à 2min au lieu du realtime qui déclenchait un full re-sync sur chaque changement DB
    // Skip si l'onglet n'est pas visible pour économiser l'egress
    const pollInterval = setInterval(() => {
      if (!isMounted) return;
      if (document.hidden) return; // ne pas poller si l'onglet est en arrière-plan
      loadData(false);
    }, 120000);

    // Écouteurs ciblés pour les changements critiques uniquement (sans full re-sync)
    const shiftsChannel = supabase.channel('shifts-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shifts' }, () => {
        if (isMounted) loadData(false);
      })
      .subscribe();

    const messagesChannel = supabase.channel('messages-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        if (isMounted) loadData(false);
      })
      .subscribe();

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      supabase.removeChannel(shiftsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [
    session?.user?.id, 
    session?.user?.email,
    session?.user?.user_metadata?.email,
    session?.user?.user_metadata?.org_id, 
    session?.user?.user_metadata?.role, 
    session?.user?.user_metadata?.staff_id, 
    currentOrgId,
    currentProjectId,
    isInitializing
  ]);

  return { isSynced, refresh: () => loadDataRef.current?.(false) };
}
